import { API, Endpoints } from '@brg/api';
import { useCallback, useState } from 'react';

export enum ConsultationScheduleOperation {
  Remove = 0,
  Add = 1,
}

export const TIMESLOT_LOCKED = 'TIMESLOT_LOCKED';
export const LIMIT_CONSULTATION_SIGNUP_EXCEEDED = 'LIMIT_CONSULTATION_SIGNUP_EXCEEDED';

export type ConsultationScheduleResult = {
  success: boolean;
  error?: string | null;
};

export interface ConsultationScheduleStudentRequest {
  lessonId: string;
  timeSlot: string;
  studentId?: string;
  operation: ConsultationScheduleOperation;
}

const pad = (value: number) => String(value).padStart(2, '0');

export const formatConsultationTimeSlot = (slotDate: Date, referenceStart?: string): string => {
  if (!referenceStart) {
    return slotDate.toISOString();
  }

  const trimmed = referenceStart.trim();
  const hasTimezone = /([zZ]|[+-]\d{2}:\d{2})$/.test(trimmed);

  if (hasTimezone) {
    const iso = slotDate.toISOString();
    if (trimmed.endsWith('Z') || trimmed.endsWith('z')) {
      if (!trimmed.includes('.')) {
        return iso.replace(/\.\d{3}Z$/, 'Z');
      }
      return iso;
    }

    const offsetMatch = trimmed.match(/([+-]\d{2}:\d{2})$/);
    const offset = offsetMatch?.[1] || '+00:00';
    const local = `${slotDate.getFullYear()}-${pad(slotDate.getMonth() + 1)}-${pad(slotDate.getDate())}`
      + `T${pad(slotDate.getHours())}:${pad(slotDate.getMinutes())}:${pad(slotDate.getSeconds())}`;
    if (trimmed.includes('.')) {
      return `${local}.000${offset}`;
    }
    return `${local}${offset}`;
  }

  const local = `${slotDate.getFullYear()}-${pad(slotDate.getMonth() + 1)}-${pad(slotDate.getDate())}`
    + `T${pad(slotDate.getHours())}:${pad(slotDate.getMinutes())}:${pad(slotDate.getSeconds())}`;
  if (trimmed.includes('.')) {
    return `${local}.000`;
  }
  return local;
};

const formatSlotTimeLabel = (date: Date) => date.toLocaleTimeString('ru-RU', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

export type ConsultationGeneratedSlot = {
  id: string;
  label: string;
  time: number;
};

export const generateConsultationSlots = (params: {
  start?: string | Date | null;
  end?: string | Date | null;
  durationMinutes?: number | null;
  maxSlots?: number | null;
  referenceStart?: string;
}): ConsultationGeneratedSlot[] => {
  const start = params.start ? new Date(params.start) : null;
  const end = params.end ? new Date(params.end) : null;
  const duration = params.durationMinutes && params.durationMinutes > 0
    ? params.durationMinutes
    : 15;
  const maxSlots = params.maxSlots && params.maxSlots > 0
    ? Math.floor(params.maxSlots)
    : null;

  if (!start || Number.isNaN(start.getTime())) {
    return [];
  }

  const endMs = end && !Number.isNaN(end.getTime()) ? end.getTime() : start.getTime();
  const capacityEndMs = maxSlots != null
    ? start.getTime() + maxSlots * duration * 60 * 1000
    : null;
  // Never generate slots past the lesson end; capacity only limits how many fit inside it.
  const effectiveEndMs = capacityEndMs != null
    ? Math.min(endMs, capacityEndMs)
    : endMs;

  if (start.getTime() >= effectiveEndMs) {
    return [];
  }

  const effectiveEnd = new Date(effectiveEndMs);
  const result: ConsultationGeneratedSlot[] = [];
  const current = new Date(start);

  while (current < effectiveEnd) {
    if (maxSlots != null && result.length >= maxSlots) break;

    const next = new Date(current.getTime() + duration * 60 * 1000);
    if (next > effectiveEnd) break;

    result.push({
      id: formatConsultationTimeSlot(current, params.referenceStart),
      label: `${formatSlotTimeLabel(current)}-${formatSlotTimeLabel(next)}`,
      time: current.getTime(),
    });
    current.setTime(next.getTime());
  }

  return result;
};

const collectErrorTexts = (errorData: unknown): string[] => {
  if (typeof errorData === 'string' && errorData) return [errorData];
  if (!errorData || typeof errorData !== 'object') return [];

  const data = errorData as Record<string, unknown>;
  const texts: string[] = [];

  ['detail', 'title', 'message', 'error', 'code', 'errorCode'].forEach((key) => {
    if (typeof data[key] === 'string' && data[key]) texts.push(data[key] as string);
  });

  const errors = data.errors;
  if (Array.isArray(errors)) {
    texts.push(...errors.filter((value): value is string => typeof value === 'string'));
  } else if (errors && typeof errors === 'object') {
    texts.push(
      ...Object.values(errors as Record<string, unknown>)
        .flat()
        .filter((value): value is string => typeof value === 'string'),
    );
  }

  return texts;
};

const getErrorMessage = (errorData: unknown) => {
  const texts = collectErrorTexts(errorData);
  if (texts.some((text) => text.includes(LIMIT_CONSULTATION_SIGNUP_EXCEEDED))) {
    return LIMIT_CONSULTATION_SIGNUP_EXCEEDED;
  }

  return texts[0] || null;
};

export const useConsultationScheduleStudent = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const scheduleStudent = useCallback(async (
    requestBody: ConsultationScheduleStudentRequest,
    url = '/api/LMS/ConsultationScheduleStudent',
  ): Promise<ConsultationScheduleResult> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await API.request()
        .endpoint(Endpoints.geoGateway)
        .url(url)
        .post()
        .body({
          lessonId: requestBody.lessonId,
          timeSlot: requestBody.timeSlot,
          studentId: requestBody.studentId ?? '',
          operation: requestBody.operation,
        })
        .send();

      if (response.status === 200) {
        return { success: true };
      }

      const errorData = await response.json().catch(() => ({}));
      const nextError = getErrorMessage(errorData) || `Error: ${response.status}`;
      setError(nextError);
      return { success: false, error: nextError };
    } catch (requestError: any) {
      const nextError = requestError?.message || 'Failed to schedule consultation student';
      setError(nextError);
      return { success: false, error: nextError };
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { scheduleStudent, isLoading, error };
};
import { API, Endpoints } from '@brg/api';
import { useCallback, useState } from 'react';

export enum ConsultationScheduleOperation {
  Remove = 0,
  Add = 1,
}

export const TIMESLOT_LOCKED = 'TIMESLOT_LOCKED';
export const LIMIT_CONSULTATION_SIGNUP_EXCEEDED = 'LIMIT_CONSULTATION_SIGNUP_EXCEEDED';

export type ConsultationScheduleResult = {
  success: boolean;
  error?: string | null;
};

export interface ConsultationScheduleStudentRequest {
  lessonId: string;
  timeSlot: string;
  studentId?: string;
  operation: ConsultationScheduleOperation;
}

const pad = (value: number) => String(value).padStart(2, '0');

export const formatConsultationTimeSlot = (slotDate: Date, referenceStart?: string): string => {
  if (!referenceStart) {
    return slotDate.toISOString();
  }

  const trimmed = referenceStart.trim();
  const hasTimezone = /([zZ]|[+-]\d{2}:\d{2})$/.test(trimmed);

  if (hasTimezone) {
    const iso = slotDate.toISOString();
    if (trimmed.endsWith('Z') || trimmed.endsWith('z')) {
      if (!trimmed.includes('.')) {
        return iso.replace(/\.\d{3}Z$/, 'Z');
      }
      return iso;
    }

    const offsetMatch = trimmed.match(/([+-]\d{2}:\d{2})$/);
    const offset = offsetMatch?.[1] || '+00:00';
    const local = `${slotDate.getFullYear()}-${pad(slotDate.getMonth() + 1)}-${pad(slotDate.getDate())}`
      + `T${pad(slotDate.getHours())}:${pad(slotDate.getMinutes())}:${pad(slotDate.getSeconds())}`;
    if (trimmed.includes('.')) {
      return `${local}.000${offset}`;
    }
    return `${local}${offset}`;
  }

  const local = `${slotDate.getFullYear()}-${pad(slotDate.getMonth() + 1)}-${pad(slotDate.getDate())}`
    + `T${pad(slotDate.getHours())}:${pad(slotDate.getMinutes())}:${pad(slotDate.getSeconds())}`;
  if (trimmed.includes('.')) {
    return `${local}.000`;
  }
  return local;
};

const formatSlotTimeLabel = (date: Date) => date.toLocaleTimeString('ru-RU', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

export type ConsultationGeneratedSlot = {
  id: string;
  label: string;
  time: number;
};

export const generateConsultationSlots = (params: {
  start?: string | Date | null;
  end?: string | Date | null;
  durationMinutes?: number | null;
  maxSlots?: number | null;
  referenceStart?: string;
}): ConsultationGeneratedSlot[] => {
  const start = params.start ? new Date(params.start) : null;
  const end = params.end ? new Date(params.end) : null;
  const duration = params.durationMinutes && params.durationMinutes > 0
    ? params.durationMinutes
    : 15;
  const maxSlots = params.maxSlots && params.maxSlots > 0
    ? Math.floor(params.maxSlots)
    : null;

  if (!start || Number.isNaN(start.getTime())) {
    return [];
  }

  const endMs = end && !Number.isNaN(end.getTime()) ? end.getTime() : start.getTime();
  const capacityEndMs = maxSlots != null
    ? start.getTime() + maxSlots * duration * 60 * 1000
    : null;
  // Never generate slots past the lesson end; capacity only limits how many fit inside it.
  const effectiveEndMs = capacityEndMs != null
    ? Math.min(endMs, capacityEndMs)
    : endMs;

  if (start.getTime() >= effectiveEndMs) {
    return [];
  }

  const effectiveEnd = new Date(effectiveEndMs);
  const result: ConsultationGeneratedSlot[] = [];
  const current = new Date(start);

  while (current < effectiveEnd) {
    if (maxSlots != null && result.length >= maxSlots) break;

    const next = new Date(current.getTime() + duration * 60 * 1000);
    if (next > effectiveEnd) break;

    result.push({
      id: formatConsultationTimeSlot(current, params.referenceStart),
      label: `${formatSlotTimeLabel(current)}-${formatSlotTimeLabel(next)}`,
      time: current.getTime(),
    });
    current.setTime(next.getTime());
  }

  return result;
};

const collectErrorTexts = (errorData: unknown): string[] => {
  if (typeof errorData === 'string' && errorData) return [errorData];
  if (!errorData || typeof errorData !== 'object') return [];

  const data = errorData as Record<string, unknown>;
  const texts: string[] = [];

  ['detail', 'title', 'message', 'error', 'code', 'errorCode'].forEach((key) => {
    if (typeof data[key] === 'string' && data[key]) texts.push(data[key] as string);
  });

  const errors = data.errors;
  if (Array.isArray(errors)) {
    texts.push(...errors.filter((value): value is string => typeof value === 'string'));
  } else if (errors && typeof errors === 'object') {
    texts.push(
      ...Object.values(errors as Record<string, unknown>)
        .flat()
        .filter((value): value is string => typeof value === 'string'),
    );
  }

  return texts;
};

const getErrorMessage = (errorData: unknown) => {
  const texts = collectErrorTexts(errorData);
  if (texts.some((text) => text.includes(LIMIT_CONSULTATION_SIGNUP_EXCEEDED))) {
    return LIMIT_CONSULTATION_SIGNUP_EXCEEDED;
  }

  return texts[0] || null;
};

export const useConsultationScheduleStudent = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const scheduleStudent = useCallback(async (
    requestBody: ConsultationScheduleStudentRequest,
    url = '/api/LMS/ConsultationScheduleStudent',
  ): Promise<ConsultationScheduleResult> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await API.request()
        .endpoint(Endpoints.geoGateway)
        .url(url)
        .post()
        .body({
          lessonId: requestBody.lessonId,
          timeSlot: requestBody.timeSlot,
          studentId: requestBody.studentId ?? '',
          operation: requestBody.operation,
        })
        .send();

      if (response.status === 200) {
        return { success: true };
      }

      const errorData = await response.json().catch(() => ({}));
      const nextError = getErrorMessage(errorData) || `Error: ${response.status}`;
      setError(nextError);
      return { success: false, error: nextError };
    } catch (requestError: any) {
      const nextError = requestError?.message || 'Failed to schedule consultation student';
      setError(nextError);
      return { success: false, error: nextError };
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { scheduleStudent, isLoading, error };
};
